import React, { FunctionComponent, useEffect, useMemo } from 'react';
import { useAsyncCallback } from 'react-async-hook';
import { startCase, uniqBy } from 'lodash';
import { STATES } from '@upstart/harmony/lib/constants';
import { DealerLaunchStepProps } from '../types';
import { TextField } from '../../Input/TextField/TextField';
import { Dealer } from '../../../types/dealerTypes';
import {
  phoneValidator,
  triggerValidation,
  zipValidator,
} from '../../../utils/validation';
import { Address } from '../../Input/Address/Address';
import { Select } from '../../Input/Select/Select';
import UtilsService, {
  GetCountiesAndCitiesResult,
} from '../../../services/UtilsService';
import { useLaunchpadForm } from '../../../hooks/useLaunchpadForm';
import { addContactInfoDefaults } from '../defaults';
import { DealerLaunchFormCardLayout } from '../DealerLaunchFormCardLayout';
import styles from '../styles.module.scss';

const getCountyOptions = (data?: GetCountiesAndCitiesResult) =>
  uniqBy(
    data?.map((item) => ({
      label: startCase(item.county.toLowerCase()),
      value: startCase(item.county.toLowerCase()),
    })) || [],
    'value',
  );

export const DealerLaunchAboutContact: FunctionComponent<DealerLaunchStepProps> = ({
  dealer,
  prevStep,
  nextStep,
}) => {
  const form = useLaunchpadForm<Dealer>({ defaultValues: dealer });

  // whenever the zip changes, get all the cities and counties
  const zip = form.watch('zip');
  const countiesAndCities = useAsyncCallback(UtilsService.getCountiesAndCities);
  useEffect(() => {
    if (zip && zipValidator.value.test(zip)) {
      countiesAndCities.execute(zip).then(({ data }) => {
        const countyOptions = getCountyOptions(data);
        // if there's only one county, auto select that
        if (countyOptions.length === 1) {
          form.setValue('county', countyOptions[0].value);
        }
        triggerValidation(form, 'county');
      });
    } else if (countiesAndCities.status === 'success') {
      countiesAndCities.reset();
      form.setValue('county', undefined);
      triggerValidation(form, 'county');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zip]);

  const countyOptions = useMemo(
    () => getCountyOptions(countiesAndCities.result?.data),
    [countiesAndCities],
  );

  return (
    <DealerLaunchFormCardLayout
      className={styles.aboutContactStep}
      form={form}
      title="How can people get in touch with you?"
      subTitle="Your dealer contact information is essential for Upstart Auto Retail's online and in store solutions to function. We use this information to connect you with customers, determine the appropriate taxes on your vehicles and more."
      cardTitle="Dealer Contact Information"
      prevStep={prevStep}
      onFormSubmit={(data) => nextStep(addContactInfoDefaults(data))}
    >
      <TextField
        label="Dealer Phone Number"
        tooltip="Your dealership's sales department phone number."
        name="phone"
        width="md"
        form={form}
        mask="(999) 999-9999"
        validation={{ required: true, pattern: phoneValidator }}
      />
      <Address
        label="Street Address"
        tooltip="Where your dealership is located."
        name="address"
        width="lg"
        form={form}
        validation={{ required: true }}
        onAddressSelect={(data) => {
          // fill in each address field into this form and trigger validation
          const fields = ['address', 'city', 'state', 'zip'] as const;
          fields.forEach((field) => {
            form.setValue(field, data[field]);
            triggerValidation(form, field);
          });
        }}
      />
      <div className={styles.stepRow}>
        <TextField
          className={styles.cityInput}
          label="City"
          name="city"
          form={form}
          validation={{ required: true }}
        />
        <Select
          label="State"
          name="state"
          form={form}
          validation={{ required: true }}
          options={Object.entries(STATES).map(([stateShort, stateLong]) => ({
            label: stateLong,
            value: stateShort,
          }))}
        />
        {zip && zipValidator.value.test(zip) && countyOptions.length === 0 ? (
          // if a valid zip is provided, but ATC doesn't provide any counties, make this a text field
          <TextField
            label="County"
            name="county"
            form={form}
            validation={{ required: true }}
            loading={countiesAndCities.status !== 'success'}
          />
        ) : (
          <Select
            label="County"
            name="county"
            form={form}
            validation={{ required: true }}
            options={countyOptions}
            addEmptyOption={countyOptions.length > 1}
          />
        )}
        <TextField
          className={styles.zipInput}
          label="Zip"
          name="zip"
          form={form}
          validation={{ required: true, pattern: zipValidator }}
        />
      </div>
    </DealerLaunchFormCardLayout>
  );
};
